// /live/grid — 多路同看(V4P-3,自 ls-v4 iPad Multi-Window 並排移植;APP#52 擴充)。
// APP#52(VistaFlow 對標):分割版面 4(2×2)/9(3×3)/16(4×4)+ 雙擊放大 + 放大(聚焦)
// 時只掛載該格、其餘 unmount → useLiveSession 收掉 session = off-focus 省流
// (對標 VistaFlow off-page pause)。每格一個 useLiveSession;不同裝置各自 1 session,
// 不觸及「每裝置 ≤4 sessions」上限(platform 02 §7.2)。單機全功能仍走 /devices/:id/live。
import { UiIcon } from '../../components/UiIcon';
import { useRef, useState } from 'react';
import { LayoutBar } from '../vms/LayoutBar';
import '../vms/vms.css';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useDeviceList } from '../devices/useDeviceList';
import { cameraClass, deviceName, tileState, type DeviceSummary } from '../devices/deviceModel';
import { useLiveSession } from './useLiveSession';
import '../devices/devices.css';

/**
 * 同時觀看的上限。
 *
 * 2026-08-20：原本是 `LAYOUTS = [4, 9, 16]` + 一排 4/9/16 選擇器，格數由使用者挑、選取上限跟著格數走。
 * 那是把實作參數當成產品選項——選了 16 格卻只有 3 台攝影機，得到的是**一片空框**，
 * 而使用者從來不想要「16 格」，他想要的是「把我選的這幾台一起看」。
 * 改成與 iOS / Android 相同：格數**依已選台數自動決定**，使用者只需要選機器。
 *
 * ⚠️ 這是**client 端**同時解碼 N 條 WebRTC 的實務上限，不是後端能力宣告——
 * capability dictionary / REST 都沒有這個數字，別把它當成平台契約往外引用。
 * 值與 iOS `LiveGridView.maxConcurrent` / Android `LIVE_GRID_MAX_CONCURRENT` 相同。
 */
const MAX_CONCURRENT = 4;

function GridCell({ d, onClose, enlarged, onToggleEnlarge }: { d: DeviceSummary; onClose: () => void; enlarged: boolean; onToggleEnlarge: () => void }) {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const { phase, error, retry } = useLiveSession(d.device_id, videoRef);
  return (
    <div
      data-testid={`grid-cell-${d.device_id}`}
      onDoubleClick={onToggleEnlarge}
      title={enlarged ? t('live.restore') : t('live.enlarge')}
      style={{ background: '#000', borderRadius: 8, overflow: 'hidden', position: 'relative', aspectRatio: '16/9', cursor: enlarged ? 'zoom-out' : 'zoom-in' }}
    >
      <video ref={videoRef} autoPlay playsInline muted style={{ width: '100%', height: '100%', objectFit: 'contain' }} data-testid={`grid-video-${d.device_id}`} />
      <div style={{ position: 'absolute', top: 8, left: 8, right: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ background: 'rgba(0,0,0,.6)', padding: '2px 8px', borderRadius: 4, fontSize: 12 }}>
          {deviceName(d)}・<span data-testid={`grid-phase-${d.device_id}`}>{phase === 'streaming' ? t('live.phaseStreaming') : phase === 'connecting' ? t('live.phaseConnecting') : phase === 'failed' ? t('live.phaseFailed') : t('live.phaseStopped')}</span>
        </span>
        <span style={{ display: 'flex', gap: 6 }}>
          <button
            className="dv-badge"
            onClick={(e) => {
              e.stopPropagation();
              onToggleEnlarge();
            }}
            aria-label={enlarged ? t('live.restore') : t('live.enlarge')}
          >
            <UiIcon name={enlarged ? 'minimize' : 'maximize'} />
          </button>
          <button
            className="dv-badge"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            aria-label={t('live.closeCell', { name: deviceName(d) })}
          >
            ✕
          </button>
        </span>
      </div>
      {phase === 'failed' && (
        <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', background: 'rgba(0,0,0,.55)' }}>
          <div style={{ textAlign: 'center' }}>
            <p>{error?.title ?? t('live.connectFailed')}</p>
            <p className="text-secondary" style={{ fontSize: 12 }}>{error?.detail}</p>
            <button
              onClick={(e) => {
                e.stopPropagation();
                retry();
              }}
            >
              {t('common.retry')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function LiveGridPage() {
  const { t } = useTranslation();
  const { devices } = useDeviceList();
  const [selected, setSelected] = useState<string[]>([]);
  const [enlarged, setEnlarged] = useState<string | null>(null);
  const cameras = (devices ?? []).filter((d) => cameraClass(d) === 'camera' && tileState(d) !== 'offline');
  const max = MAX_CONCURRENT;

  const toggle = (id: string) => {
    setSelected((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : cur.length >= max ? cur : [...cur, id]));
  };
  const picked = selected.map((id) => (devices ?? []).find((d) => d.device_id === id)).filter((d): d is DeviceSummary => d != null);
  // 放大(聚焦):只顯示該格(其餘 unmount→暫停);目標已移除則退回全部。
  const focus = enlarged && picked.some((d) => d.device_id === enlarged) ? enlarged : null;
  const shown = focus ? picked.filter((d) => d.device_id === focus) : picked;
  // 1–2 台直排（每格更大）；3–4 台 2×2。放大檢視時只有一格。與 iOS/Android 同一條規則。
  const cols = shown.length <= 2 ? 1 : 2;

  return (
    <section>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h1>{t('live.gridTitle')}</h1>
          <p className="text-secondary">
            {t('live.gridSubtitlePrefix', { count: max })}
            <Link to="/devices">{t('common.devices')}</Link>
            {t('live.gridSubtitleSuffix')}
          </p>
        </div>
      </header>

      {/* ls-loop MV.3：存 / 開版面（含裝置分組）；只開在線的、最多 max 台 */}
      <LayoutBar devices={devices} openable={cameras.map((d) => d.device_id)} max={max} selected={selected} onOpen={(ids) => { setEnlarged(null); setSelected(ids); }} />

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '12px 0' }} data-testid="grid-picker">
        {cameras.map((d) => (
          <button
            key={d.device_id}
            onClick={() => toggle(d.device_id)}
            aria-pressed={selected.includes(d.device_id)}
            data-testid={`grid-pick-${d.device_id}`}
            className="pick-chip"
          >
            {deviceName(d)}
          </button>
        ))}
        {cameras.length === 0 && <p className="text-secondary">{t('live.noCamerasToWatch')}</p>}
      </div>

      {focus && (
        <p className="text-secondary" role="status" style={{ margin: '0 0 8px', fontSize: 'var(--font-size-sm)' }}>
          {t('live.focusPausedHint')}
        </p>
      )}

      <div
        data-testid="grid-stage"
        style={{ display: 'grid', gap: 12, gridTemplateColumns: shown.length <= 1 ? '1fr' : `repeat(${cols}, 1fr)` }}
      >
        {shown.map((d) => (
          <GridCell
            key={d.device_id}
            d={d}
            onClose={() => toggle(d.device_id)}
            enlarged={focus === d.device_id}
            onToggleEnlarge={() => setEnlarged((cur) => (cur === d.device_id ? null : d.device_id))}
          />
        ))}
      </div>
      {picked.length === 0 && <p className="text-secondary">{t('live.pickCamerasHint', { count: max })}</p>}
    </section>
  );
}
